import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  User, Activity, Ruler, Heart, Calendar, TrendingUp, 
  MessageCircle, Bell, ChevronRight, Award, Target
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';
import AppTutorial from './AppTutorial';

const API = `${BACKEND_URL}/api`;

export default function ParentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [linkedChildren, setLinkedChildren] = useState([]);
  const [childrenData, setChildrenData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedChild, setSelectedChild] = useState(null);

  useEffect(() => {
    loadLinkedChildren();
  }, []);

  const loadLinkedChildren = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Get linked children for this parent
      const response = await axios.get(`${API}/parent-linking/my-children`, { headers });
      setLinkedChildren(response.data.children || []);
      
      if (response.data.children?.length > 0) {
        setSelectedChild(response.data.children[0]);
        
        // Load data for each child
        const dataPromises = response.data.children.map(async (child) => {
          try {
            const [measurementsRes, testsRes] = await Promise.all([
              axios.get(`${API}/children/${child.id}/measurements`, { headers }).catch(() => ({ data: [] })),
              axios.get(`${API}/children/${child.id}/athletic-tests/summary`, { headers }).catch(() => ({ data: {} }))
            ]);
            
            return {
              id: child.id,
              measurements: measurementsRes.data,
              testSummary: testsRes.data
            };
          } catch (err) {
            return { id: child.id, measurements: [], testSummary: {} };
          }
        });
        
        const allData = await Promise.all(dataPromises);
        const dataMap = {};
        allData.forEach(d => { dataMap[d.id] = d; });
        setChildrenData(dataMap);
      }
    } catch (err) {
      console.error('Failed to load linked children:', err);
      if (err.response?.status === 404) {
        // No children linked yet
        setLinkedChildren([]);
      } else {
        toast.error('Failed to load your children');
      }
    } finally {
      setLoading(false);
    }
  };

  const getLatestMeasurement = (childId) => {
    const data = childrenData[childId];
    if (!data?.measurements?.length) return null;
    return data.measurements[0];
  };

  const getTestSummary = (childId) => {
    return childrenData[childId]?.testSummary || {};
  };

  const calculateAge = (dob) => {
    if (!dob) return null;
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-zinc-500 font-mono animate-pulse">Loading your dashboard...</div>
        <AppTutorial />
      </div>
    );
  }

  if (linkedChildren.length === 0) {
    return (
      <div className="min-h-screen bg-[#050505] grid-texture">
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="w-24 h-24 bg-zinc-900 rounded-full flex items-center justify-center mx-auto mb-6">
            <User className="h-12 w-12 text-zinc-600" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">No Children Linked</h1>
          <p className="text-zinc-400 mb-8">
            You haven't linked any children to your account yet. Search for your child to get started.
          </p>
          <Button
            onClick={() => navigate('/parent-registration')}
            className="bg-[#02b5ff] hover:bg-[#02b5ff]/90 text-white"
          >
            Link Your Child
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        {/* App Tutorial - always show for parents */}
        <AppTutorial />
      </div>
    );
  }

  const currentChild = selectedChild || linkedChildren[0];
  const latestMeasurement = getLatestMeasurement(currentChild?.id);
  const testSummary = getTestSummary(currentChild?.id);

  return (
    <div className="min-h-screen bg-[#050505] grid-texture" data-testid="parent-dashboard">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-950/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-zinc-500">Welcome back,</p>
              <h1 className="text-2xl font-bold text-white">{user?.full_name || 'Parent'}</h1>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/parent-registration')}
              className="border-zinc-700 text-zinc-300 hover:text-white"
            >
              <User className="mr-2 h-4 w-4" />
              Link Another Child
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Child Selector (if multiple children) */}
        {linkedChildren.length > 1 && (
          <div className="mb-6 flex gap-3 overflow-x-auto pb-2">
            {linkedChildren.map((child) => (
              <button
                key={child.id}
                onClick={() => setSelectedChild(child)}
                className={`px-4 py-2 rounded-full border text-sm font-medium whitespace-nowrap transition-all ${
                  currentChild?.id === child.id
                    ? 'bg-[#02b5ff]/20 border-[#02b5ff] text-[#02b5ff]'
                    : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-zinc-500'
                }`}
              >
                {child.first_name} {child.last_name}
              </button>
            ))}
          </div>
        )}

        {/* Child Profile Card */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 mb-6">
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 bg-gradient-to-br from-[#02b5ff] to-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-3xl font-bold text-white">
                {currentChild?.first_name?.[0]}{currentChild?.last_name?.[0]}
              </span>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white">
                {currentChild?.first_name} {currentChild?.last_name}
              </h2>
              <div className="flex flex-wrap gap-4 mt-2 text-sm text-zinc-400">
                {currentChild?.date_of_birth && (
                  <span>Age: {calculateAge(currentChild.date_of_birth)} years</span>
                )}
                {currentChild?.current_team_name && (
                  <span>Team: {currentChild.current_team_name}</span>
                )}
              </div>
              <Button
                onClick={() => navigate(`/child/${currentChild?.id}`)}
                className="mt-4 bg-[#02b5ff] hover:bg-[#02b5ff]/90 text-white"
                data-testid="view-full-profile-btn"
              >
                View Full Profile
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {/* Height */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Ruler className="h-4 w-4 text-[#02b5ff]" />
              <span className="text-xs text-zinc-500 uppercase tracking-wider">Height</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {latestMeasurement?.standing_height ? `${latestMeasurement.standing_height} cm` : '—'}
            </p>
            {latestMeasurement?.uk_who_percentiles?.height_percentile && (
              <p className="text-xs text-zinc-500 mt-1">
                {latestMeasurement.uk_who_percentiles.height_percentile.toFixed(0)}th percentile
              </p>
            )}
          </div>

          {/* Weight */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="h-4 w-4 text-green-500" />
              <span className="text-xs text-zinc-500 uppercase tracking-wider">Weight</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {latestMeasurement?.weight ? `${latestMeasurement.weight} kg` : '—'}
            </p>
            {latestMeasurement?.uk_who_percentiles?.weight_percentile && (
              <p className="text-xs text-zinc-500 mt-1">
                {latestMeasurement.uk_who_percentiles.weight_percentile.toFixed(0)}th percentile
              </p>
            )}
          </div>

          {/* Performance Grade */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Award className="h-4 w-4 text-yellow-500" />
              <span className="text-xs text-zinc-500 uppercase tracking-wider">Avg Grade</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {testSummary?.overall_average_grade ? `${testSummary.overall_average_grade}/5` : '—'}
            </p>
            {testSummary?.total_tests > 0 && (
              <p className="text-xs text-zinc-500 mt-1">
                {testSummary.total_tests} tests recorded
              </p>
            )}
          </div>

          {/* Last Update */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4 text-purple-500" />
              <span className="text-xs text-zinc-500 uppercase tracking-wider">Last Update</span>
            </div>
            <p className="text-lg font-bold text-white">
              {latestMeasurement?.measurement_date 
                ? new Date(latestMeasurement.measurement_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                : '—'}
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate(`/child/${currentChild?.id}?tab=measurements`)}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-left hover:border-[#02b5ff]/30 transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#02b5ff]/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-[#02b5ff]" />
              </div>
              <h3 className="text-white font-semibold group-hover:text-[#02b5ff] transition-colors">
                Growth Tracking
              </h3>
            </div>
            <p className="text-sm text-zinc-500">View height and weight progress over time</p>
          </button>

          <button
            onClick={() => navigate(`/child/${currentChild?.id}?tab=ai-coach`)}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-left hover:border-[#02b5ff]/30 transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Target className="h-5 w-5 text-purple-500" />
              </div>
              <h3 className="text-white font-semibold group-hover:text-purple-400 transition-colors">
                Training Plans
              </h3>
            </div>
            <p className="text-sm text-zinc-500">AI-powered training recommendations</p>
          </button>

          <button
            onClick={() => navigate(`/child/${currentChild?.id}?tab=medical`)}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-left hover:border-[#02b5ff]/30 transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-pink-500/20 rounded-lg flex items-center justify-center">
                <Heart className="h-5 w-5 text-pink-500" />
              </div>
              <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">
                Health & Medical
              </h3>
            </div>
            <p className="text-sm text-zinc-500">Medical questionnaire and health status</p>
          </button>
        </div>

        {/* Communication Section */}
        <div className="mt-6 bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <MessageCircle className="h-5 w-5 text-[#02b5ff]" />
              <h3 className="text-lg font-bold text-white">Messages</h3>
            </div>
            <Button
              variant="ghost"
              className="text-[#02b5ff] hover:text-[#02b5ff]/80"
            >
              View All
            </Button>
          </div>
          <p className="text-zinc-500 text-center py-8">
            No new messages from coaches
          </p>
        </div>
      </div>
      
      {/* App Tutorial */}
      <AppTutorial />
    </div>
  );
}
